import React, { useState, useEffect } from 'react';
import { db, collection, getDocs, doc, getDoc } from './firebase';
import { getTotalStudyMaterialCount } from '../utils/firebaseStats';
import { motion } from 'framer-motion';
import { FiUsers, FiTrendingUp, FiAward, FiCode, FiArrowRight } from 'react-icons/fi';
import CountUp from 'react-countup';
import { useTheme } from '../context/ThemeContext';

const Dashboard = () => {
  useTheme();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalMaterials: 0,
    averageRating: 0,
    activeProjects: 0
  });
  const [loading, setLoading] = useState(true);

  const c = {
    bg: 'var(--bg-main)',
    card: 'var(--bg-card)',
    border: 'var(--border)',
    accent: 'var(--accent)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)'
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // 1. Fetch Students Count
        const usersSnap = await getDocs(collection(db, 'Users'));
        const totalUsers = usersSnap.size;

        // 2. Fetch Average Rating from Feedback
        const feedbackSnap = await getDocs(collection(db, 'feedback'));
        let avgRating = 4.8;
        if (feedbackSnap.size > 0) {
          const totalRating = feedbackSnap.docs.reduce((acc, d) => acc + (Number(d.data().rating) || 0), 0);
          avgRating = (totalRating / feedbackSnap.size).toFixed(1);
        }

        // 3. Fetch Contributions Count
        const contribSnap = await getDocs(collection(db, 'contributions'));
        const totalContributions = contribSnap.size;

        // 4. Fetch Accurate Study Materials Count
        const deepMaterialsCount = await getTotalStudyMaterialCount();

        setStats({
          totalUsers: totalUsers,
          totalMaterials: deepMaterialsCount,
          averageRating: parseFloat(avgRating),
          activeProjects: totalContributions > 0 ? totalContributions : 12
        });

        // 5. Try fetching from metadata if it exists (manual overrides)
        const statsDoc = await getDoc(doc(db, 'metadata', 'stats'));
        if (statsDoc.exists()) {
          const data = statsDoc.data();
          setStats(prev => ({
            ...prev,
            totalUsers: data.totalUsers || prev.totalUsers,
            totalMaterials: data.totalMaterials || prev.totalMaterials,
            averageRating: data.averageRating || prev.averageRating,
            activeProjects: data.activeProjects || prev.activeProjects
          }));
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const StatCard = ({ icon, label, value, sub, delay, suffix = "" }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.6 }}
      whileHover={{ y: -5, backgroundColor: 'var(--glass)' }}
      style={{
        background: c.card,
        border: `1px solid ${c.border}`,
        borderRadius: 24, padding: 24,
        display: 'flex', flexDirection: 'column', gap: 20,
        backdropFilter: 'blur(16px)',
        transition: 'all 0.3s ease'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: c.accent
        }}>
          {icon}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--text-main)' }}>
            {loading ? '...' : <CountUp end={value} duration={2} decimals={suffix === '★' ? 1 : 0} suffix={suffix} />}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{sub}</div>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-main)' }}>{label}</div>
      </div>
    </motion.div>
  );

  return (
    <div style={{ padding: '80px 24px', position: 'relative', overflow: 'hidden', background: c.bg }}>
      {/* Background Decor */}
      <div style={{ position: 'absolute', top: '10%', right: '5%', width: 400, height: 400, background: 'rgba(34, 197, 94, 0.05)', filter: 'blur(100px)', borderRadius: '50%', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <motion.div
          style={{ textAlign: 'center', marginBottom: 64 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '8px 20px', borderRadius: 50,
            background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.2)',
            color: c.accent, fontSize: '0.8rem', fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 24
          }}>
            🚀 Impact Dashboard
          </div>
          <h2 style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: 900, letterSpacing: '-0.04em', color: 'var(--text-main)', margin: '0 0 16px', lineHeight: 1.1 }}>
            Study <span style={{ color: 'var(--accent)' }}>Smarter</span>, Code Better
          </h2>
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)', color: c.muted, maxWidth: 600, margin: '0 auto', lineHeight: 1.6, fontWeight: 500 }}>
            Join the most trusted community of passionate learners and developers pushing the boundaries of technology.
          </p>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 20, marginBottom: 64
        }}>
          <StatCard
            icon={<FiUsers size={22} />}
            label="Total Members"
            value={stats.totalUsers || 1200}
            sub="Happy Learners"
            delay={0.1}
          />
          <StatCard
            icon={<FiTrendingUp size={22} />}
            label="Contributions"
            value={stats.activeProjects || 12}
            sub="Currently Live"
            delay={0.2}
          />
          <StatCard
            icon={<FiAward size={22} />}
            label="User Satisfaction"
            value={stats.averageRating || 4.8}
            sub="Community Loved"
            delay={0.3}
            suffix="★"
          />
          <StatCard
            icon={<FiCode size={22} />}
            label="Study Material"
            value={stats.totalMaterials || 500}
            sub="Resource Rich"
            delay={0.4}
            suffix="+"
          />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          style={{
            background: 'rgba(34, 197, 94, 0.05)',
            border: `1px solid rgba(34, 197, 94, 0.2)`,
            borderRadius: 32, padding: '48px 24px',
            textAlign: 'center',
            position: 'relative', overflow: 'hidden'
          }}
        >
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h3 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 900, color: 'var(--text-main)', marginBottom: 16 }}>Ready to grow together?</h3>
            <p style={{ color: c.muted, maxWidth: 480, margin: '0 auto 32px', lineHeight: 1.6, fontWeight: 500 }}>
              Join our WhatsApp community to get instant updates, ask questions, and network with fellow IPU students.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <motion.button
                onClick={() => window.open("https://chat.whatsapp.com/BcJOVpVibwKEU3kjVsYLJ3?mode=ac_t", "_blank")}
                whileHover={{ scale: 1.05, backgroundColor: '#16a34a' }}
                whileTap={{ scale: 0.95 }}
                style={{
                  padding: '16px 40px', borderRadius: 16,
                  background: c.accent, color: '#fff',
                  border: 'none', fontWeight: 900, fontSize: '1rem',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                  boxShadow: '0 20px 40px rgba(34, 197, 94, 0.3)'
                }}
              >
                Join WhatsApp Community <FiArrowRight size={20} />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;